import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { APIResult, emptyAPIResult, loadingAPIResult, successAPIResult, errorAPIResult } from '../../lib/redux';
import { triplePSDatasource } from '../../datasource';
import { ChatAction, ChatMessage, Conversation, ConversationSummary, KnowledgeNote } from '../../models';

export interface ChatState {
  isOpen: boolean;
  model: string;
  conversationId?: string;
  messages: ChatMessage[];
  costUsd?: string;
  /** incremented each time a new send starts so the poll effect re-arms */
  pollNonce: number;
  send: APIResult<{ conversationId: string; messageId: string }>;
  poll: APIResult<Conversation>;
  conversations: ConversationSummary[];
  listConversations: APIResult<ConversationSummary[]>;
  /** Proposals waiting for review */
  pendingNotes: KnowledgeNote[];
  /** Approved notes that are injected into the system prompt */
  activeNotes: KnowledgeNote[];
  fetchKnowledge: APIResult<KnowledgeNote[]>;
  updateKnowledge: APIResult<KnowledgeNote>;
  /** Write-action proposals from the AI */
  actions: ChatAction[];
  fetchActions: APIResult<ChatAction[]>;
  confirmAction: APIResult<ChatAction>;
  cancelAction: APIResult<ChatAction>;
}

const initialState: ChatState = {
  isOpen: false,
  model: 'claude-opus-4-8',
  conversationId: undefined,
  messages: [],
  costUsd: undefined,
  pollNonce: 0,
  send: emptyAPIResult(),
  poll: emptyAPIResult(),
  conversations: [],
  listConversations: emptyAPIResult(),
  pendingNotes: [],
  activeNotes: [],
  fetchKnowledge: emptyAPIResult(),
  updateKnowledge: emptyAPIResult(),
  actions: [],
  fetchActions: emptyAPIResult(),
  confirmAction: emptyAPIResult(),
  cancelAction: emptyAPIResult(),
};

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (args: { message: string }, thunkAPI) => {
    const state = thunkAPI.getState() as { chat: ChatState };
    const { model, conversationId } = state.chat;
    try {
      return await triplePSDatasource.sendChatMessage({
        message: args.message,
        model,
        conversationId,
      });
    } catch (err: any) {
      return thunkAPI.rejectWithValue({
        status: err?.response?.status as number | undefined,
        message: (err?.message ?? '') as string,
      });
    }
  }
);

export const pollConversation = createAsyncThunk(
  'chat/pollConversation',
  async (args: { conversationId: string }) => {
    return triplePSDatasource.getConversation(args.conversationId);
  }
);

export const fetchConversations = createAsyncThunk(
  'chat/fetchConversations',
  async () => {
    return triplePSDatasource.listConversations();
  }
);

export const loadConversation = createAsyncThunk(
  'chat/loadConversation',
  async (args: { conversationId: string }, thunkAPI) => {
    // Load the conversation messages immediately
    const result = await triplePSDatasource.getConversation(args.conversationId);
    return { conversationId: args.conversationId, conversation: result };
  }
);

/** Loads both pending and active notes in parallel. */
export const fetchKnowledgeNotes = createAsyncThunk(
  'chat/fetchKnowledgeNotes',
  async () => {
    const [pending, active] = await Promise.all([
      triplePSDatasource.listKnowledgeNotes('pending'),
      triplePSDatasource.listKnowledgeNotes('active'),
    ]);
    return { pending, active };
  }
);

/** Approve/reject/edit a note. On success, the caller patches state. */
export const updateKnowledgeNote = createAsyncThunk(
  'chat/updateKnowledgeNote',
  async (args: { id: string; status?: 'pending' | 'active' | 'rejected'; content?: string }) => {
    const updated = await triplePSDatasource.updateKnowledgeNote(args.id, {
      status: args.status,
      content: args.content,
    });
    return updated;
  }
);

/** Fetch all actions for the current conversation. */
export const fetchChatActions = createAsyncThunk(
  'chat/fetchChatActions',
  async (args: { conversationId: string }) => {
    return triplePSDatasource.listActions(args.conversationId);
  }
);

/** Confirm (execute) a proposed action. */
export const confirmChatAction = createAsyncThunk(
  'chat/confirmChatAction',
  async (args: { id: string }) => {
    return triplePSDatasource.confirmAction(args.id);
  }
);

/** Cancel a proposed action. */
export const cancelChatAction = createAsyncThunk(
  'chat/cancelChatAction',
  async (args: { id: string }) => {
    return triplePSDatasource.cancelAction(args.id);
  }
);

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    openChat(state) {
      state.isOpen = true;
    },
    closeChat(state) {
      state.isOpen = false;
    },
    setModel(state, action) {
      state.model = action.payload;
    },
    newChat(state) {
      state.conversationId = undefined;
      state.messages = [];
      state.costUsd = undefined;
      state.pollNonce += 1;
      state.send = emptyAPIResult();
      state.poll = emptyAPIResult();
      state.actions = [];
      state.fetchActions = emptyAPIResult();
      state.confirmAction = emptyAPIResult();
      state.cancelAction = emptyAPIResult();
    },
  },
  extraReducers: builder => {
    builder
      .addCase(sendMessage.pending, state => {
        state.send = loadingAPIResult();
        // increment nonce so the poll effect re-arms even if isRunning was already true (I1)
        state.pollNonce += 1;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.send = successAPIResult(action.payload);
        // persist conversationId if it was not set yet
        if (!state.conversationId) {
          state.conversationId = action.payload.conversationId;
        }
        // C1: append the user message and a running assistant placeholder so
        // isRunning becomes true and the poll effect kicks off immediately.
        state.messages = [
          ...state.messages,
          {
            id: `user-${action.payload.messageId}`,
            role: 'user' as const,
            status: '' as const,
            content: action.meta.arg.message,
          },
          {
            id: action.payload.messageId,
            role: 'assistant' as const,
            status: 'running' as const,
            content: '',
          },
        ];
      })
      .addCase(sendMessage.rejected, (state, action) => {
        // action.error is always a SerializedError; the structured
        // { status, message } payload is surfaced to the caller via unwrap()
        // and read there (see ChatPanel handleSend catch block).
        state.send = errorAPIResult(action.error);
      })
      .addCase(pollConversation.pending, state => {
        state.poll = loadingAPIResult();
      })
      .addCase(pollConversation.fulfilled, (state, action) => {
        state.poll = successAPIResult(action.payload);
        state.messages = action.payload.messages;
        if (action.payload.costUsd != null) {
          state.costUsd = action.payload.costUsd;
        }
      })
      .addCase(pollConversation.rejected, (state, action) => {
        state.poll = errorAPIResult(action.error);
      })
      .addCase(fetchConversations.pending, state => {
        state.listConversations = loadingAPIResult();
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.listConversations = successAPIResult(action.payload);
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.listConversations = errorAPIResult(action.error);
      })
      .addCase(loadConversation.pending, state => {
        // Clear stale messages immediately so the old conversation isn't visible
        // during the getConversation round-trip.
        state.messages = [];
        state.costUsd = undefined;
        state.poll = loadingAPIResult();
        // Clear stale actions from any previous conversation
        state.actions = [];
        state.fetchActions = emptyAPIResult();
      })
      .addCase(loadConversation.fulfilled, (state, action) => {
        state.conversationId = action.payload.conversationId;
        state.messages = action.payload.conversation.messages;
        if (action.payload.conversation.costUsd != null) {
          state.costUsd = action.payload.conversation.costUsd;
        }
        // Only bump pollNonce if the loaded conversation has a running assistant
        // message — a completed history conversation doesn't need a poll re-run.
        const hasRunning = action.payload.conversation.messages.some(
          (m: ChatMessage) => m.role === 'assistant' && m.status === 'running'
        );
        if (hasRunning) {
          state.pollNonce += 1;
        }
        state.send = emptyAPIResult();
        state.poll = emptyAPIResult();
      })
      .addCase(loadConversation.rejected, (state, action) => {
        state.poll = errorAPIResult(action.error);
      })

      // Knowledge notes
      .addCase(fetchKnowledgeNotes.pending, state => {
        state.fetchKnowledge = loadingAPIResult();
      })
      .addCase(fetchKnowledgeNotes.fulfilled, (state, action) => {
        state.fetchKnowledge = successAPIResult([...action.payload.pending, ...action.payload.active]);
        state.pendingNotes = action.payload.pending;
        state.activeNotes = action.payload.active;
      })
      .addCase(fetchKnowledgeNotes.rejected, (state, action) => {
        state.fetchKnowledge = errorAPIResult(action.error);
      })
      .addCase(updateKnowledgeNote.pending, state => {
        state.updateKnowledge = loadingAPIResult();
      })
      .addCase(updateKnowledgeNote.fulfilled, (state, action) => {
        const updated = action.payload;
        state.updateKnowledge = successAPIResult(updated);

        // Remove from pending regardless of outcome
        state.pendingNotes = state.pendingNotes.filter(n => n.id !== updated.id);
        // Remove from active (in case of Remove/reject)
        state.activeNotes = state.activeNotes.filter(n => n.id !== updated.id);

        if (updated.status === 'active') {
          // Approve: move to active (or update existing active)
          state.activeNotes = [...state.activeNotes, updated];
        } else if (updated.status === 'pending') {
          // Content edit on a pending note: put it back in pending
          state.pendingNotes = [...state.pendingNotes, updated];
        }
        // status === 'rejected': removed from both lists (already done above)
      })
      .addCase(updateKnowledgeNote.rejected, (state, action) => {
        state.updateKnowledge = errorAPIResult(action.error);
      })

      // Chat actions
      .addCase(fetchChatActions.pending, state => {
        state.fetchActions = loadingAPIResult();
      })
      .addCase(fetchChatActions.fulfilled, (state, action) => {
        state.fetchActions = successAPIResult(action.payload);
        state.actions = action.payload;
      })
      .addCase(fetchChatActions.rejected, (state, action) => {
        state.fetchActions = errorAPIResult(action.error);
      })
      .addCase(confirmChatAction.pending, state => {
        state.confirmAction = loadingAPIResult();
      })
      .addCase(confirmChatAction.fulfilled, (state, action) => {
        const updated = action.payload;
        state.confirmAction = successAPIResult(updated);
        state.actions = state.actions.map(a => a.id === updated.id ? updated : a);
      })
      .addCase(confirmChatAction.rejected, (state, action) => {
        state.confirmAction = errorAPIResult(action.error);
      })
      .addCase(cancelChatAction.pending, state => {
        state.cancelAction = loadingAPIResult();
      })
      .addCase(cancelChatAction.fulfilled, (state, action) => {
        const updated = action.payload;
        state.cancelAction = successAPIResult(updated);
        state.actions = state.actions.map(a => a.id === updated.id ? updated : a);
      })
      .addCase(cancelChatAction.rejected, (state, action) => {
        state.cancelAction = errorAPIResult(action.error);
      });
  },
});

export const { openChat, closeChat, setModel, newChat } = chatSlice.actions;

export const selectChatOpen = (state: { chat: ChatState }) => state.chat.isOpen;
export const selectChatMessages = (state: { chat: ChatState }) => state.chat.messages;
export const selectChatModel = (state: { chat: ChatState }) => state.chat.model;
export const selectPollNonce = (state: { chat: ChatState }) => state.chat.pollNonce;
export const selectIsRunning = (state: { chat: ChatState }) =>
  state.chat.messages.some(m => m.role === 'assistant' && m.status === 'running');
export const selectConversations = (state: { chat: ChatState }) => state.chat.conversations;
export const selectChatCost = (state: { chat: ChatState }) => state.chat.costUsd;
export const selectPendingNotes = (state: { chat: ChatState }) => state.chat.pendingNotes ?? [];
export const selectActiveNotes = (state: { chat: ChatState }) => state.chat.activeNotes ?? [];
export const selectFetchKnowledgeLoading = (state: { chat: ChatState }) =>
  state.chat.fetchKnowledge?.loading ?? false;

export const selectChatActions = (state: { chat: ChatState }) => state.chat.actions ?? [];
export const selectPendingChatActions = (state: { chat: ChatState }) =>
  (state.chat.actions ?? []).filter(a => a.status === 'pending');

export default chatSlice.reducer;
